import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Interface translation.
 *
 * Deliberately hand-rolled and tiny rather than pulling in i18next. The
 * interface strings are a fixed, known set; what actually varies at runtime is
 * *case content*, and that is translated server-side by the LLM pipeline. A
 * full i18n framework would add weight without touching the hard part.
 *
 * Missing keys fall back to English and are logged in development, so an
 * untranslated string shows readable English rather than a raw key. A worker
 * seeing `grievance.status.in_progress` on screen is worse than seeing
 * "In progress".
 */

const STORAGE_KEY = 'gp.locale'

export const LOCALES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
]

const strings = {
  en: {
    'app.name': 'Grievance Portal',
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.email': 'Email address',
    'app.emailOrPhone': 'Email or phone number',
    'app.password': 'Password',
    'app.search': 'Search',
    'app.filter': 'Filter',
    'app.clear': 'Clear',
    'app.save': 'Save',
    'app.cancel': 'Cancel',
    'app.submit': 'Submit',
    'app.close': 'Close',
    'app.back': 'Back',
    'app.next': 'Next',
    'app.loading': 'Loading…',
    'app.retry': 'Try again',
    'app.export': 'Export',
    'app.all': 'All',
    'app.none': 'None',
    'app.optional': 'optional',
    'app.required': 'required',

    'nav.dashboard': 'Dashboard',
    'nav.myGrievances': 'My grievances',
    'nav.fileGrievance': 'Raise a grievance',
    'nav.queue': 'Grievance queue',
    'nav.assignedToMe': 'Assigned to me',
    'nav.enquiries': 'WhatsApp callbacks',
    'nav.discipline': 'Disciplinary cases',
    'nav.grievanceDiscipline': 'Grievance & Discipline',
    'nav.sectionCasework': 'Casework',
    'nav.sectionConfidential': 'Confidential',
    'nav.sectionAdmin': 'Administration',
    'nav.myDiscipline': 'My record',
    'nav.feedback': 'Feedback',
    'nav.suggestionBox': 'Suggestion box',
    'nav.surveys': 'Surveys',
    'nav.posh': 'POSH',
    'nav.poshDashboard': 'POSH dashboard',
    'nav.iccRoster': 'ICC roster',
    'nav.poshComplaints': 'POSH complaints',
    'nav.poshTraining': 'Training compliance',
    'nav.poshAnnualReport': 'Annual report',
    'nav.whistleblower': 'Whistleblower',
    'nav.reports': 'Reports',
    'nav.people': 'People',
    'nav.settings': 'Settings',

    'grievance.ticket': 'Ticket',
    'grievance.subject': 'Subject',
    'grievance.category': 'Category',
    'grievance.status': 'Status',
    'grievance.priority': 'Priority',
    'grievance.filedOn': 'Filed on',
    'grievance.officer': 'Officer',
    'grievance.dueBy': 'Due by',
    'grievance.overdue': 'Overdue',
    'grievance.atRisk': 'At risk',
    'grievance.describe': 'Describe what happened',

    'status.open': 'Open',
    'status.acknowledged': 'Acknowledged',
    'status.in_progress': 'In progress',
    // Phrased from the employee's side, because this is the one status where
    // the next move is theirs. "Awaiting employee" reads as a queue label; the
    // person looking at it needs to understand they are the hold-up.
    'status.awaiting_employee': 'Waiting for your reply',
    'status.resolved': 'Resolved',
    'status.closed': 'Closed',
    'status.reopened': 'Reopened',
    'status.withdrawn': 'Withdrawn',

    'priority.low': 'Low',
    'priority.medium': 'Medium',
    'priority.high': 'High',
    'priority.critical': 'Critical',

    'empty.noGrievances': 'No grievances yet',
    'empty.noResults': 'Nothing matches those filters',
  },

  hi: {
    'app.name': 'शिकायत पोर्टल',
    'app.signIn': 'साइन इन करें',
    'app.signOut': 'साइन आउट',
    'app.email': 'ईमेल पता',
    'app.emailOrPhone': 'ईमेल या फ़ोन नंबर',
    'app.password': 'पासवर्ड',
    'app.search': 'खोजें',
    'app.filter': 'फ़िल्टर',
    'app.clear': 'साफ़ करें',
    'app.save': 'सहेजें',
    'app.cancel': 'रद्द करें',
    'app.submit': 'जमा करें',
    'app.close': 'बंद करें',
    'app.back': 'वापस',
    'app.next': 'आगे',
    'app.loading': 'लोड हो रहा है…',
    'app.retry': 'पुनः प्रयास करें',
    'app.export': 'निर्यात',
    'app.all': 'सभी',
    'app.none': 'कोई नहीं',
    'app.optional': 'वैकल्पिक',
    'app.required': 'आवश्यक',

    'nav.dashboard': 'डैशबोर्ड',
    'nav.myGrievances': 'मेरी शिकायतें',
    'nav.fileGrievance': 'शिकायत दर्ज करें',
    'nav.queue': 'शिकायत सूची',
    'nav.assignedToMe': 'मुझे सौंपी गई',
    'nav.enquiries': 'व्हाट्सएप कॉलबैक',
    'nav.discipline': 'अनुशासनात्मक मामले',
    'nav.grievanceDiscipline': 'शिकायत और अनुशासन',
    'nav.sectionCasework': 'कार्यभार',
    'nav.sectionConfidential': 'गोपनीय',
    'nav.sectionAdmin': 'प्रशासन',
    'nav.myDiscipline': 'मेरा रिकॉर्ड',
    'nav.feedback': 'प्रतिक्रिया',
    'nav.suggestionBox': 'सुझाव पेटी',
    'nav.surveys': 'सर्वेक्षण',
    'nav.posh': 'पॉश',
    'nav.poshDashboard': 'पॉश डैशबोर्ड',
    'nav.iccRoster': 'आईसीसी सदस्य',
    'nav.poshComplaints': 'पॉश शिकायतें',
    'nav.poshTraining': 'प्रशिक्षण अनुपालन',
    'nav.poshAnnualReport': 'वार्षिक रिपोर्ट',
    'nav.whistleblower': 'व्हिसलब्लोअर',
    'nav.reports': 'रिपोर्ट',
    'nav.people': 'कर्मचारी',
    'nav.settings': 'सेटिंग्स',

    'grievance.ticket': 'टिकट',
    'grievance.subject': 'विषय',
    'grievance.category': 'श्रेणी',
    'grievance.status': 'स्थिति',
    'grievance.priority': 'प्राथमिकता',
    'grievance.filedOn': 'दर्ज दिनांक',
    'grievance.officer': 'अधिकारी',
    'grievance.dueBy': 'नियत तिथि',
    'grievance.overdue': 'विलंबित',
    'grievance.atRisk': 'जोखिम में',
    'grievance.describe': 'क्या हुआ, विस्तार से बताएं',

    'status.open': 'खुला',
    'status.acknowledged': 'स्वीकृत',
    'status.in_progress': 'प्रगति पर',
    'status.awaiting_employee': 'आपके उत्तर की प्रतीक्षा',
    'status.resolved': 'हल हो गया',
    'status.closed': 'बंद',
    'status.reopened': 'पुनः खोला गया',
    'status.withdrawn': 'वापस लिया',

    'priority.low': 'कम',
    'priority.medium': 'मध्यम',
    'priority.high': 'उच्च',
    'priority.critical': 'अति गंभीर',

    'empty.noGrievances': 'अभी कोई शिकायत नहीं',
    'empty.noResults': 'इन फ़िल्टर से कुछ नहीं मिला',
  },

  mr: {
    'app.name': 'तक्रार पोर्टल',
    'app.signIn': 'साइन इन करा',
    'app.signOut': 'साइन आउट',
    'app.email': 'ईमेल पत्ता',
    'app.emailOrPhone': 'ईमेल किंवा फोन नंबर',
    'app.password': 'पासवर्ड',
    'app.search': 'शोधा',
    'app.filter': 'फिल्टर',
    'app.clear': 'साफ करा',
    'app.save': 'जतन करा',
    'app.cancel': 'रद्द करा',
    'app.submit': 'सादर करा',
    'app.close': 'बंद करा',
    'app.back': 'मागे',
    'app.next': 'पुढे',
    'app.loading': 'लोड होत आहे…',
    'app.retry': 'पुन्हा प्रयत्न करा',
    'app.export': 'निर्यात',
    'app.all': 'सर्व',
    'app.none': 'काहीही नाही',
    'app.optional': 'ऐच्छिक',
    'app.required': 'आवश्यक',

    'nav.dashboard': 'डॅशबोर्ड',
    'nav.myGrievances': 'माझ्या तक्रारी',
    'nav.fileGrievance': 'तक्रार नोंदवा',
    'nav.queue': 'तक्रार यादी',
    'nav.assignedToMe': 'मला दिलेल्या',
    'nav.enquiries': 'व्हॉट्सअॅप कॉलबॅक',
    'nav.discipline': 'शिस्तभंग प्रकरणे',
    'nav.grievanceDiscipline': 'तक्रार व शिस्तभंग',
    'nav.sectionCasework': 'कामकाज',
    'nav.sectionConfidential': 'गोपनीय',
    'nav.sectionAdmin': 'प्रशासन',
    'nav.myDiscipline': 'माझी नोंद',
    'nav.feedback': 'अभिप्राय',
    'nav.suggestionBox': 'सूचना पेटी',
    'nav.surveys': 'सर्वेक्षण',
    'nav.posh': 'पॉश',
    'nav.poshDashboard': 'पॉश डॅशबोर्ड',
    'nav.iccRoster': 'आयसीसी सदस्य',
    'nav.poshComplaints': 'पॉश तक्रारी',
    'nav.poshTraining': 'प्रशिक्षण अनुपालन',
    'nav.poshAnnualReport': 'वार्षिक अहवाल',
    'nav.whistleblower': 'व्हिसलब्लोअर',
    'nav.reports': 'अहवाल',
    'nav.people': 'कर्मचारी',
    'nav.settings': 'सेटिंग्ज',

    'grievance.ticket': 'तिकीट',
    'grievance.subject': 'विषय',
    'grievance.category': 'श्रेणी',
    'grievance.status': 'स्थिती',
    'grievance.priority': 'प्राधान्य',
    'grievance.filedOn': 'नोंद दिनांक',
    'grievance.officer': 'अधिकारी',
    'grievance.dueBy': 'मुदत',
    'grievance.overdue': 'विलंबित',
    'grievance.atRisk': 'धोक्यात',
    'grievance.describe': 'काय घडले ते सांगा',

    'status.open': 'उघडे',
    'status.acknowledged': 'स्वीकारले',
    'status.in_progress': 'प्रगतीपथावर',
    'status.awaiting_employee': 'तुमच्या उत्तराची प्रतीक्षा',
    'status.resolved': 'निकाली',
    'status.closed': 'बंद',
    'status.reopened': 'पुन्हा उघडले',
    'status.withdrawn': 'मागे घेतले',

    'priority.low': 'कमी',
    'priority.medium': 'मध्यम',
    'priority.high': 'उच्च',
    'priority.critical': 'अत्यंत गंभीर',

    'empty.noGrievances': 'अद्याप तक्रार नाही',
    'empty.noResults': 'या फिल्टरशी काहीही जुळत नाही',
  },
}

const I18nContext = createContext(null)

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && strings[stored]) return stored

    // Fall back to the browser's language when it is one we speak, so a Hindi
    // phone gets a Hindi portal without anyone configuring anything.
    const browser = navigator.language?.slice(0, 2)
    return strings[browser] ? browser : 'en'
  })

  const setLocale = useCallback((next) => {
    if (!strings[next]) return
    setLocaleState(next)
    localStorage.setItem(STORAGE_KEY, next)
  }, [])

  useEffect(() => {
    // Keeps the document language honest for screen readers and for the
    // browser's own hyphenation and font selection.
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo(() => {
    const table = strings[locale] ?? strings.en

    const t = (key, fallback) => {
      if (table[key] !== undefined) return table[key]

      if (import.meta.env.DEV && strings.en[key] === undefined && fallback === undefined) {
        // Only warn for keys that exist nowhere — a missing translation is
        // expected and handled, a missing key is a typo.
        console.warn(`[i18n] unknown key: ${key}`)
      }

      return strings.en[key] ?? fallback ?? key
    }

    return { locale, setLocale, t, locales: LOCALES }
  }, [locale, setLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error('useI18n must be used inside an I18nProvider.')
  }

  return context
}
